import SpeakButton from "@/components/SpeakButton";
import { useState } from "react";
import { useT } from "@/i18n";
import { SAMPLE, ISO, APERTURE, SPEED, PHOTO_TEXT } from "@/components/photo/content";

const NOISE = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

const Slider = ({ label, values, idx, setIdx, color, testId }) => (
  <div className="bg-white rounded-2xl p-4 shadow-md">
    <div className="flex justify-between items-center">
      <span className="font-fredoka font-bold text-blue-950">{label}</span>
      <span className={`${color} rounded-full px-3 py-0.5 font-bold text-blue-950 text-sm`} data-testid={`${testId}-value`}>{values[idx]}</span>
    </div>
    <input type="range" min={0} max={values.length - 1} value={idx} onChange={(e) => setIdx(Number(e.target.value))} className="w-full mt-3 accent-blue-600" data-testid={testId} />
  </div>
);

export default function ExposureTriangle() {
  const { lang } = useT();
  const tx = PHOTO_TEXT[lang];
  const [iso, setIso] = useState(2);
  const [ap, setAp] = useState(3);
  const [sp, setSp] = useState(4);
  const score = iso + (6 - ap) + sp - 9;
  const brightness = Math.min(2.2, Math.max(0.15, 1 + score * 0.14));
  const status = score <= -2 ? tx.dark : score >= 2 ? tx.bright : tx.perfect;
  const ok = Math.abs(score) < 2;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2"><h3 className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950">{tx.triangle}</h3><SpeakButton compact mascot="lumi" text={`${tx.triangle} ${tx.triangleSub}`} testId="ask-photo-triangle" /></div>
      <p className="text-blue-900 font-semibold">{tx.triangleSub}</p>
      <div className="mt-4 grid lg:grid-cols-2 gap-5">
        <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-lg aspect-[3/2]" data-testid="exposure-preview">
          <img src={SAMPLE} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: `brightness(${brightness}) blur(${sp > 4 ? (sp - 4) * 1.2 : 0}px)` }} />
          <img src={SAMPLE} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: `brightness(${brightness}) blur(${(6 - ap) * 1.3}px)`, WebkitMaskImage: "radial-gradient(circle at 37% 50%, transparent 22%, black 45%)", maskImage: "radial-gradient(circle at 37% 50%, transparent 22%, black 45%)" }} />
          <div className="absolute inset-0 mix-blend-overlay pointer-events-none" style={{ backgroundImage: NOISE, opacity: iso / 7 }} />
          <div className={`absolute bottom-3 left-3 right-3 rounded-2xl px-4 py-2 font-bold text-sm ${ok ? "bg-lime-200 text-green-900" : "bg-white/90 text-blue-950"}`} data-testid="exposure-status">{status}</div>
        </div>
        <div className="space-y-3">
          <Slider label="ISO" values={ISO} idx={iso} setIdx={setIso} color="bg-yellow-200" testId="slider-iso" />
          <p className="text-sm font-semibold text-blue-900 px-1">{tx.iso}</p>
          <Slider label={lang === "es" ? "Apertura" : "Aperture"} values={APERTURE} idx={ap} setIdx={setAp} color="bg-pink-200" testId="slider-aperture" />
          <p className="text-sm font-semibold text-blue-900 px-1">{tx.aperture}</p>
          <Slider label={lang === "es" ? "Velocidad" : "Shutter speed"} values={SPEED} idx={sp} setIdx={setSp} color="bg-sky-200" testId="slider-speed" />
          <p className="text-sm font-semibold text-blue-900 px-1">{tx.speed}</p>
        </div>
      </div>
    </div>
  );
}
